// DEPENDENCIES ---------------------------------------------------------------- //

import { Moves } from '@gql/base-types';

// TYPES ---------------------------------------------------------------- //

export interface ConfirmationSummary {
  pickup: {
    status: string; // Raw database status
    attempts: number;
  };
  delivery: {
    status: string; // Raw database status
    attempts: number;
  };
}

// UTILITY ---------------------------------------------------------------- //

/**
 * Determines if a confirmation request is for pickup or delivery
 * Based on workflow_id matching pickup_workflow_id or delivery_workflow_id
 */
const isPickupConfirmation = (
  confirmationRequest: any,
  confirmationset: any
): boolean => {
  if (!confirmationRequest?.workflow?.id || !confirmationset) return false;

  // Check if workflow_id matches pickup_workflow_id
  if (confirmationset.pickup_workflow_id === confirmationRequest.workflow.id) {
    return true;
  }

  // Check if workflow_id matches delivery_workflow_id
  if (confirmationset.delivery_workflow_id === confirmationRequest.workflow.id) {
    return false;
  }

  // Fallback: check workflow name/label for pickup/delivery keywords
  const workflowName = confirmationRequest.workflow?.name?.toLowerCase() || '';
  const workflowLabel = confirmationRequest.workflow?.label?.toLowerCase() || '';

  if (workflowName.includes('pickup') || workflowLabel.includes('pickup')) {
    return true;
  }

  return false;
};

/**
 * Gets the most recent confirmation request status for pickup or delivery
 * Returns raw database status directly - no mapping
 */
const getLatestConfirmationStatus = (
  confirmationRequests: any[],
  confirmationset: any,
  isPickup: boolean
): { status: string; attempts: number } => {
  if (!confirmationRequests || confirmationRequests.length === 0) {
    return { status: 'pending', attempts: 0 };
  }

  // Filter requests by pickup/delivery
  const filteredRequests = confirmationRequests.filter(req => isPickupConfirmation(req, confirmationset) === isPickup);

  if (filteredRequests.length === 0) {
    return { status: 'pending', attempts: 0 };
  }

  // Get the most recent request (they're already ordered by trigger_time desc)
  const latestRequest = filteredRequests[0];
  // Use raw database status directly - no mapping
  const status = latestRequest.status || 'pending';

  return {
    status,
    attempts: filteredRequests.length,
  };
};

/**
 * Extracts confirmation summary from a move
 */
export const getConfirmationSummary = (move: Moves | null | undefined): ConfirmationSummary | null => {
  if (!move) return null;

  // Get scenario from scenariotomoves
  const scenarioToMove = move.scenariotomoves?.[0];
  if (!scenarioToMove?.scenario) return null;

  const scenario = scenarioToMove.scenario;
  const confirmationRequests = scenario.confirmationrequests || [];

  // Get confirmationset from the first confirmation request (they should all have the same one)
  const confirmationset = confirmationRequests[0]?.confirmationset;

  // Get pickup and delivery confirmation statuses
  const pickup = getLatestConfirmationStatus(confirmationRequests, confirmationset, true);
  const delivery = getLatestConfirmationStatus(confirmationRequests, confirmationset, false);

  return {
    pickup,
    delivery,
  };
};
